// ABOUTME: API client module for Hopp — pennant CRUD + polling against Valkey backend
// ABOUTME: Maps directly to the FastAPI surface defined in api/main.py

import { getCanvasConfig, getPollingConfig } from './config.js';
import { classifyFetchError, classifyResponseError } from './api-errors.js';
import { parseStatsCount } from './pennant-stats.js';

const API_URL = import.meta.env.VITE_API_URL;

let apiBaseUrl = '';
let pollingInterval = null;
let knownPennantIds = new Set();
let isOfflineMode = false;

export function initApiClient() {
    if (!API_URL) {
        console.warn('VITE_API_URL not configured. Running in offline mode.');
        isOfflineMode = true;
        return null;
    }
    apiBaseUrl = API_URL;
    isOfflineMode = false;
    console.log(`API client initialized: ${apiBaseUrl}`);
    return true;
}

export function getApiBaseUrl() {
    return apiBaseUrl;
}

export async function fetchRecentPennants(limit = 50) {
    if (isOfflineMode) return [];
    try {
        const response = await fetch(`${apiBaseUrl}/pennants/recent?limit=${limit}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching recent pennants:', error);
        return [];
    }
}

export async function fetchPennantsBatch(cursor = '0', limit = 500, loaded = 0) {
    if (isOfflineMode) {
        return { pennants: [], cursor: '0', total: 0, hasMore: false };
    }
    try {
        const response = await fetch(
            `${apiBaseUrl}/pennants/paginated?cursor=${cursor}&limit=${limit}&loaded=${loaded}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching pennant batch:', error);
        return { pennants: [], cursor: '0', total: 0, hasMore: false };
    }
}

// Authoritative total pennant count from GET /stats — the real DB number,
// independent of how many pennants are fetched/rendered. Returns null when
// offline (DB-less demo) or on failure so the caller keeps its local count.
export async function fetchStats() {
    if (isOfflineMode) return null;
    try {
        const response = await fetch(`${apiBaseUrl}/stats`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return parseStatsCount(await response.json());
    } catch (error) {
        console.error('Error fetching stats:', error);
        return null;
    }
}

export async function fetchDensityCenter() {
    if (isOfflineMode) {
        const config = getCanvasConfig();
        return { x: config.initialSize / 2, y: config.initialSize / 2 };
    }
    try {
        const response = await fetch(`${apiBaseUrl}/pennants/density-center`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching density center:', error);
        const config = getCanvasConfig();
        return { x: config.initialSize / 2, y: config.initialSize / 2 };
    }
}

export async function fetchCanvasConfig() {
    const config = getCanvasConfig();
    return { width: config.initialSize, height: config.initialSize };
}

function getBrowserLocale() {
    return navigator.language || navigator.userLanguage || null;
}

function getCountryFromLocale(locale) {
    if (!locale) return null;
    const parts = locale.split('-');
    return parts.length > 1 ? parts[1].toUpperCase() : null;
}

export async function createPennant({ x, y, team, rotation = 0, brightness = 1.0, ownerToken = null }) {
    const locale = getBrowserLocale();
    const country = getCountryFromLocale(locale);

    if (isOfflineMode) {
        return {
            id: crypto.randomUUID(),
            x, y, team, rotation, brightness,
            created_at: new Date().toISOString(),
            locale, country,
            owner_token: ownerToken,
        };
    }

    const body = { x, y, team, rotation, brightness, locale, country };
    if (ownerToken) body.owner_token = ownerToken;

    let response;
    try {
        response = await fetch(`${apiBaseUrl}/pennant`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch (fetchError) {
        throw classifyFetchError(fetchError);
    }

    if (!response.ok) {
        throw classifyResponseError(response);
    }

    return await response.json();
}

export async function getPennant(id) {
    if (isOfflineMode || !id) return null;
    try {
        const response = await fetch(`${apiBaseUrl}/pennant/${encodeURIComponent(id)}`);
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching pennant:', error);
        return null;
    }
}

export function markPennantKnown(id) {
    knownPennantIds.add(id);
}

export function initializeKnownPennants(pennants) {
    knownPennantIds.clear();
    for (const p of pennants) {
        knownPennantIds.add(p.id);
    }
}

export async function subscribeToPennants(onInsert) {
    if (isOfflineMode) return null;

    const intervalMs = getPollingConfig()?.intervalMs || 4000;
    console.log(`Starting pennant polling every ${intervalMs}ms`);

    pollingInterval = setInterval(async () => {
        try {
            const pennants = await fetchRecentPennants(50);
            for (const p of pennants) {
                if (!knownPennantIds.has(p.id)) {
                    knownPennantIds.add(p.id);
                    if (onInsert) onInsert(p);
                }
            }
        } catch (error) {
            console.error('Error polling recent pennants:', error);
        }
    }, intervalMs);

    return { polling: true, interval: intervalMs };
}

export function unsubscribe() {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
}
